import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const TIPO_CONFIG = {
  VISITA: { label: "Visita", icon: "mdi:map-marker-outline", color: "#0284c7" },
  REUNION: { label: "Reunión", icon: "mdi:account-group-outline", color: "#d97706" },
  TAREA: { label: "Tarea", icon: "mdi:clipboard-check-outline", color: "#16a34a" },
  OTRO: { label: "Otro", icon: "mdi:dots-horizontal-circle-outline", color: "#9333ea" },
};

const ESTADO_CONFIG = {
  PENDIENTE: {
    label: "Pendiente",
    class: "bg-warning-focus text-warning-main",
    icon: "mdi:clock-outline",
  },
  COMPLETADA: {
    label: "Completada",
    class: "bg-success-focus text-success-main",
    icon: "mdi:check-circle-outline",
  },
  CANCELADA: {
    label: "Cancelada",
    class: "bg-danger-focus text-danger-main",
    icon: "mdi:cancel",
  },
};

const AgendaEventDetailModal = ({
  show,
  onHide,
  event,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  if (!show || !event) return null;

  const tipo = TIPO_CONFIG[event.tipo] || TIPO_CONFIG.OTRO;
  const estado = ESTADO_CONFIG[event.estado] || ESTADO_CONFIG.PENDIENTE;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">
          {/* Header con color del tipo */}
          <div
            className="modal-header py-20 px-24 border-0"
            style={{
              background: `linear-gradient(135deg, ${tipo.color}15, ${tipo.color}08)`,
              borderBottom: `3px solid ${tipo.color}`,
            }}
          >
            <div className="d-flex align-items-center gap-12">
              <div
                className="w-44-px h-44-px d-flex align-items-center justify-content-center radius-10"
                style={{ backgroundColor: `${tipo.color}20` }}
              >
                <Icon icon={tipo.icon} style={{ color: tipo.color, fontSize: "22px" }} />
              </div>
              <div>
                <h5 className="modal-title fw-semibold mb-2">{event.titulo}</h5>
                <div className="d-flex align-items-center gap-8">
                  <span
                    className="text-xs fw-medium px-8 py-2 radius-4"
                    style={{
                      color: tipo.color,
                      backgroundColor: `${tipo.color}15`,
                    }}
                  >
                    {tipo.label}
                  </span>
                  <span
                    className={`text-xs fw-medium px-8 py-2 radius-4 d-flex align-items-center gap-4 ${estado.class}`}
                  >
                    <Icon icon={estado.icon} className="text-sm" />
                    {estado.label}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onHide} />
          </div>

          {/* Body */}
          <div className="modal-body px-24 py-20">
            {/* Fecha y hora */}
            <div className="d-flex align-items-start gap-12 mb-20">
              <div className="w-36-px h-36-px bg-primary-50 d-flex align-items-center justify-content-center radius-8 flex-shrink-0 mt-2">
                <Icon icon="mdi:calendar-clock" className="text-primary-600 text-lg" />
              </div>
              <div>
                <span className="text-secondary-light text-xs fw-medium d-block mb-4">
                  Fecha y hora
                </span>
                <span className="text-primary-light fw-semibold text-sm">
                  {formatDateTimeFull(event.fecha_hora_inicio)}
                </span>
                {event.fecha_hora_fin && (
                  <span className="text-secondary-light text-sm">
                    {" — "}
                    {formatDateTimeFull(event.fecha_hora_fin)}
                  </span>
                )}
              </div>
            </div>

            {/* Lugar */}
            {event.lugar && (
              <div className="d-flex align-items-start gap-12 mb-20">
                <div className="w-36-px h-36-px bg-info-50 d-flex align-items-center justify-content-center radius-8 flex-shrink-0 mt-2">
                  <Icon icon="mdi:map-marker-outline" className="text-info-600 text-lg" />
                </div>
                <div>
                  <span className="text-secondary-light text-xs fw-medium d-block mb-4">
                    Lugar
                  </span>
                  <span className="text-primary-light fw-semibold text-sm">
                    {event.lugar}
                  </span>
                </div>
              </div>
            )}

            {/* Creado por */}
            <div className="d-flex align-items-start gap-12 mb-20">
              <div className="w-36-px h-36-px bg-warning-50 d-flex align-items-center justify-content-center radius-8 flex-shrink-0 mt-2">
                <Icon
                  icon="mdi:account-tie-outline"
                  className="text-warning-600 text-lg"
                />
              </div>
              <div>
                <span className="text-secondary-light text-xs fw-medium d-block mb-4">
                  Creado por
                </span>
                <span className="text-primary-light fw-semibold text-sm">
                  {event.creado_por_nombre || "—"}
                </span>
              </div>
            </div>

            {/* Asignado a */}
            {event.asignado_a_nombre && (
              <div className="d-flex align-items-start gap-12 mb-20">
                <div className="w-36-px h-36-px bg-success-50 d-flex align-items-center justify-content-center radius-8 flex-shrink-0 mt-2">
                  <Icon
                    icon="mdi:account-school-outline"
                    className="text-success-600 text-lg"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-xs fw-medium d-block mb-4">
                    Asignado a
                  </span>
                  <span className="text-primary-light fw-semibold text-sm">
                    {event.asignado_a_nombre}
                  </span>
                </div>
              </div>
            )}

            {/* Descripción */}
            {event.descripcion && (
              <div className="d-flex align-items-start gap-12 mb-20">
                <div className="w-36-px h-36-px bg-lilac-50 d-flex align-items-center justify-content-center radius-8 flex-shrink-0 mt-2">
                  <Icon
                    icon="mdi:text-box-outline"
                    className="text-lilac-600 text-lg"
                  />
                </div>
                <div>
                  <span className="text-secondary-light text-xs fw-medium d-block mb-4">
                    Descripción
                  </span>
                  <p className="text-primary-light text-sm mb-0 lh-base">
                    {event.descripcion}
                  </p>
                </div>
              </div>
            )}

            {/* Acciones rápidas de estado */}
            {event.puede_modificar && (
              <div className="border-top pt-20 mt-8">
                <span className="text-secondary-light text-xs fw-medium d-block mb-12">
                  Cambiar estado rápido
                </span>
                <div className="d-flex flex-wrap gap-8">
                  {event.estado !== "PENDIENTE" && (
                    <button
                      className="btn btn-outline-warning btn-sm radius-8 d-flex align-items-center gap-4"
                      onClick={() => onStatusChange(event.id, "PENDIENTE")}
                    >
                      <Icon icon="mdi:clock-outline" className="text-base" />
                      Pendiente
                    </button>
                  )}
                  {event.estado !== "COMPLETADA" && (
                    <button
                      className="btn btn-outline-success btn-sm radius-8 d-flex align-items-center gap-4"
                      onClick={() => onStatusChange(event.id, "COMPLETADA")}
                    >
                      <Icon icon="mdi:check-circle-outline" className="text-base" />
                      Completar
                    </button>
                  )}
                  {event.estado !== "CANCELADA" && (
                    <button
                      className="btn btn-outline-danger btn-sm radius-8 d-flex align-items-center gap-4"
                      onClick={() => onStatusChange(event.id, "CANCELADA")}
                    >
                      <Icon icon="mdi:cancel" className="text-base" />
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {event.puede_modificar && (
            <div className="modal-footer px-24 py-16 border-top">
              <button
                type="button"
                className="btn btn-outline-danger radius-8 px-16 d-flex align-items-center gap-6"
                onClick={onDelete}
              >
                <Icon icon="mdi:trash-can-outline" className="text-lg" />
                Eliminar
              </button>
              <button
                type="button"
                className="btn btn-primary radius-8 px-20 d-flex align-items-center gap-6"
                onClick={onEdit}
              >
                <Icon icon="mdi:pencil-outline" className="text-lg" />
                Editar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function formatDateTimeFull(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("es-MX", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export default AgendaEventDetailModal;
